import { useState } from 'react';
import { toast } from 'sonner';
import { Eraser, KeyRound, Loader2, PauseCircle, PlayCircle, ShieldOff } from 'lucide-react';

import {
  useAdminUserHoldMutation,
  useAdminUserEraseMutation,
  useAdminUserMfaResetMutation,
  useAdminUserUnverifyAllMutation,
  VERIFICATION_FIELDS,
} from '@/client/api/admin/users.mutation';
import { useAdminUserMfaQuery } from '@/client/api/admin/users.query';
import type { AdminUserAccount } from '@/client/types/admin/users-orgs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { PERMISSIONS } from '@/client/config/permissions';

type DetailUser = AdminUserAccount & { uid: number };

const DangerZoneTab = ({
  user,
  onErased,
}: {
  user: DetailUser;
  onErased: () => void;
}) => {
  const fullName =
    [user.first_name, user.last_name].filter(Boolean).join(' ') || 'this user';
  const isSuspended = user.admin_hold === 1;

  const holdMutation = useAdminUserHoldMutation();
  const eraseMutation = useAdminUserEraseMutation();
  const unverifyMutation = useAdminUserUnverifyAllMutation();

  const verifiedCount = VERIFICATION_FIELDS.filter(field => user[field] === 1).length;

  const [holdReason, setHoldReason] = useState('');
  const [eraseReason, setEraseReason] = useState('');
  const [confirmErase, setConfirmErase] = useState(false);
  const [confirmUnverify, setConfirmUnverify] = useState(false);

  const mfa = useAdminUserMfaQuery(user.uid);
  const mfaReset = useAdminUserMfaResetMutation();
  const [mfaReason, setMfaReason] = useState('');

  const handleMfaReset = async () => {
    const toastId = toast.loading('Turning off two-step verification…');
    try {
      await mfaReset.mutateAsync({ uid: user.uid, reason: mfaReason.trim() });
      toast.success('Two-step verification turned off', {
        id: toastId,
        description: `${fullName} can sign in with a password alone and has been emailed.`,
        richColors: true,
        duration: 4000,
      });
      setMfaReason('');
    } catch (error: any) {
      toast.error('Failed to turn off two-step verification', {
        id: toastId,
        description: error?.message ?? 'Something went wrong.',
        richColors: true,
        duration: 5000,
      });
    }
  };

  const handleToggleHold = async () => {
    const toastId = toast.loading(isSuspended ? 'Lifting suspension…' : 'Suspending user…');
    try {
      await holdMutation.mutateAsync({
        uid: user.uid,
        hold: !isSuspended,
        reason: holdReason || undefined,
      });
      toast.success(isSuspended ? 'Suspension lifted' : 'User suspended', {
        id: toastId,
        description: isSuspended
          ? `${fullName} can transact again.`
          : `${fullName} can no longer transact. The account is otherwise intact.`,
        richColors: true,
        duration: 4000,
      });
      setHoldReason('');
    } catch (error: any) {
      toast.error('Failed to change suspension', {
        id: toastId,
        description: error?.message ?? 'Something went wrong.',
        richColors: true,
        duration: 5000,
      });
    }
  };

  const handleUnverifyAll = async () => {
    setConfirmUnverify(false);
    const toastId = toast.loading('Clearing verification…');
    try {
      await unverifyMutation.mutateAsync({ uid: user.uid });
      toast.success('Verification cleared', {
        id: toastId,
        description: `Every verified field for ${fullName} was reset to unverified.`,
        richColors: true,
        duration: 4000,
      });
    } catch (error: any) {
      toast.error('Failed to clear verification', {
        id: toastId,
        description: error?.message ?? 'Something went wrong.',
        richColors: true,
        duration: 5000,
      });
    }
  };

  const handleErase = async () => {
    setConfirmErase(false);
    const toastId = toast.loading('Erasing user…');
    try {
      const result = await eraseMutation.mutateAsync({
        uid: user.uid,
        reason: eraseReason || undefined,
      });
      const payload = result?.data;
      toast.success(payload?.already_erased ? 'User was already erased' : 'User erased', {
        id: toastId,
        description: `Personal data has been anonymized${
          payload?.history_rows_scrubbed
            ? ` (${payload.history_rows_scrubbed} history rows scrubbed)`
            : ''
        }.`,
        richColors: true,
        duration: 5000,
      });
      setEraseReason('');
      onErased();
    } catch (error: any) {
      toast.error('Failed to erase user', {
        id: toastId,
        description: error?.message ?? 'Something went wrong.',
        richColors: true,
        duration: 5000,
      });
    }
  };

  return (
    <div className="grid max-w-3xl gap-6">
      <section className="space-y-3 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          {isSuspended ? (
            <PlayCircle className="h-4 w-4 text-green-600" />
          ) : (
            <PauseCircle className="h-4 w-4 text-amber-600" />
          )}
          {isSuspended ? 'Reactivate' : 'Suspend'}
        </h2>
        <p className="text-xs text-gray-500">
          Suspension sets <code>admin_hold</code>: the user <strong>cannot transact</strong>.
          They can still sign in, and the account stays active — this is not deactivation.
        </p>
        <div className="space-y-1">
          <Label htmlFor="hold-reason">Reason (optional)</Label>
          <Input
            id="hold-reason"
            value={holdReason}
            onChange={e => setHoldReason(e.target.value)}
            placeholder="Recorded in the audit log"
          />
        </div>
        <PermissionButton
          code={PERMISSIONS.user.edit}
          onClick={handleToggleHold}
          disabled={holdMutation.isPending}
          className={
            isSuspended
              ? 'w-full bg-green-600 text-white hover:bg-green-700'
              : 'w-full bg-amber-600 text-white hover:bg-amber-700'
          }
        >
          {holdMutation.isPending ? (
            <>
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              Working…
            </>
          ) : isSuspended ? (
            'Lift suspension'
          ) : (
            'Suspend (cannot transact)'
          )}
        </PermissionButton>
      </section>

      <section className="space-y-3 rounded-lg border border-amber-200 bg-white p-6 shadow-sm">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-amber-700">
          <KeyRound className="h-4 w-4" />
          Two-step verification
        </h2>
        <p className="text-xs text-gray-500">
          {mfa.isLoading
            ? 'Checking…'
            : mfa.data?.enabled
              ? `On — sign-in codes go by ${mfa.data.method === 'sms' ? 'text message' : 'email'}.`
              : 'Off for this user.'}{' '}
          Turn it off only for a user who has lost the phone or mailbox their codes go to, after
          confirming who they are. They are emailed, and the reason is recorded in the audit log.
        </p>
        {mfa.data?.enabled && (
          <div className="space-y-1">
            <Label htmlFor="mfa-reason">Reason (required)</Label>
            <Input
              id="mfa-reason"
              value={mfaReason}
              onChange={e => setMfaReason(e.target.value)}
              placeholder="e.g. Lost phone; identity confirmed on support call"
            />
          </div>
        )}
        <PermissionButton
          code={PERMISSIONS.user.edit}
          variant="outline"
          onClick={handleMfaReset}
          disabled={!mfa.data?.enabled || !mfaReason.trim() || mfaReset.isPending}
          className="w-full border-amber-200 text-amber-700 hover:bg-amber-50"
        >
          {mfaReset.isPending ? (
            <>
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              Working…
            </>
          ) : mfa.data?.enabled ? (
            'Turn off two-step verification'
          ) : (
            'Nothing to turn off'
          )}
        </PermissionButton>
      </section>

      <section className="space-y-3 rounded-lg border border-amber-200 bg-white p-6 shadow-sm">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-amber-700">
          <ShieldOff className="h-4 w-4" />
          Clear all verification
        </h2>
        <p className="text-xs text-gray-500">
          Resets every verification flag — primary/secondary email &amp; phone, ID, photo,
          address and age — back to <strong>unverified</strong>. The account and its data are
          untouched; the user simply has to re-verify. Recorded in the audit log.
        </p>
        <p className="text-xs text-gray-500">
          Currently verified: <strong>{verifiedCount}</strong> of {VERIFICATION_FIELDS.length}{' '}
          fields.
        </p>
        <PermissionButton
          code={PERMISSIONS.user.edit}
          variant="outline"
          onClick={() => setConfirmUnverify(true)}
          disabled={unverifyMutation.isPending || verifiedCount === 0}
          className="w-full border-amber-200 text-amber-700 hover:bg-amber-50"
        >
          {unverifyMutation.isPending ? (
            <>
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              Working…
            </>
          ) : verifiedCount === 0 ? (
            'Nothing to clear'
          ) : (
            'Un-verify all fields'
          )}
        </PermissionButton>
      </section>

      <section className="space-y-3 rounded-lg border border-red-200 bg-white p-6 shadow-sm">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-red-700">
          <Eraser className="h-4 w-4" />
          Erase personal data
        </h2>
        <p className="text-xs text-gray-500">
          Anonymizes this account&apos;s personal data and scrubs historical records. It is
          irreversible, and the admin API has no restore path. The account row itself is not
          deleted.
        </p>
        <div className="space-y-1">
          <Label htmlFor="erase-reason">Reason (optional)</Label>
          <Input
            id="erase-reason"
            value={eraseReason}
            onChange={e => setEraseReason(e.target.value)}
            placeholder="Recorded in the audit log"
          />
        </div>
        <PermissionButton
          code={PERMISSIONS.user.delete}
          variant="outline"
          onClick={() => setConfirmErase(true)}
          disabled={eraseMutation.isPending}
          className="w-full border-red-200 text-red-600 hover:bg-red-50"
        >
          Erase user data…
        </PermissionButton>
      </section>

      <AlertDialog open={confirmUnverify} onOpenChange={setConfirmUnverify}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear all verification for {fullName}?</AlertDialogTitle>
            <AlertDialogDescription>
              Every verification flag (email, phone, ID, photo, address and age) will be set to
              unverified. The account and its data are untouched and the user can be re-verified
              afterwards. This is recorded in the audit log.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={e => {
                e.preventDefault();
                handleUnverifyAll();
              }}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Un-verify all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmErase} onOpenChange={setConfirmErase}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Erase {fullName}&apos;s data?</AlertDialogTitle>
            <AlertDialogDescription>
              This irreversibly anonymizes the account&apos;s personal data and scrubs its
              historical records. There is no undo and no restore endpoint. The account itself
              is not deleted — it remains with anonymized fields.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={e => {
                e.preventDefault();
                handleErase();
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Erase
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DangerZoneTab;
